import { useQuery } from "@tanstack/react-query";
import { Skeleton } from "@/components/ui/skeleton";
import { formatCurrency } from "@/lib/utils";
import { ArrowUp, ArrowDown, Blocks, KeySquare, DollarSign, WrenchIcon, AlertTriangle } from "lucide-react";
import { DashboardStats } from "@shared/schema";
import { useLocation } from "wouter";
import { Badge } from "@/components/ui/badge";

type SummaryCardProps = {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  iconColor: string;
  changeValue: string;
  isIncrease: boolean;
  changeText: string;
  href?: string;
};

function SummaryCard({
  title,
  value,
  icon,
  iconColor,
  changeValue,
  isIncrease,
  changeText,
  href,
}: SummaryCardProps) {
  const [, navigate] = useLocation();

  return (
    <div
      className={`bg-white rounded-lg shadow-sm border border-slate-200 p-6 transition duration-150 hover:shadow-md ${href ? 'cursor-pointer hover:border-blue-300 hover:bg-blue-50/30' : ''}`}
      onClick={href ? () => navigate(href) : undefined}
      role={href ? "button" : undefined}
      tabIndex={href ? 0 : undefined}
      onKeyDown={href ? (e) => e.key === 'Enter' && navigate(href) : undefined}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-slate-500">{title}</p>
          <h3 className="text-2xl font-bold text-slate-800 mt-1">{value}</h3>
        </div>
        <div className={`w-12 h-12 ${iconColor} rounded-full flex items-center justify-center`}>
          {icon}
        </div>
      </div>
      <div className="mt-3 flex items-center text-xs font-medium">
        <span className={`flex items-center ${isIncrease ? 'text-success' : 'text-error'}`}>
          {isIncrease ? <ArrowUp className="w-3 h-3 mr-1" /> : <ArrowDown className="w-3 h-3 mr-1" />}
          {changeValue}
        </span>
        <span className="text-slate-500 ml-2">{changeText}</span>
      </div>
      {href && (
        <p className="text-xs text-blue-500 mt-2 font-medium">Click to view →</p>
      )}
    </div>
  );
}

export default function SummaryCards() {
  const defaultStats: DashboardStats = {
    totalApplications: 0,
    activeLicenses: 0,
    totalMonthlyCost: 0,
    optimizationPotential: 0,
    todayRenewals: 0,
    todayRenewalsCost: 0,
    weekRenewals: 0,
    weekRenewalsCost: 0,
    applicationsGrowth: 0,
    licensesGrowth: 0,
    missedRenewals: 0,
    missedRenewalsCost: 0,
  };

  const { data, isLoading, error } = useQuery<Partial<DashboardStats>>({
    queryKey: ['/api/dashboard/stats'],
  });

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-responsive mb-6">
        {[...Array(4)].map((_, index) => (
          <div key={index} className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
            <div className="flex items-center justify-between">
              <div className="space-y-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-7 w-16" />
              </div>
              <Skeleton className="h-12 w-12 rounded-full" />
            </div>
            <div className="mt-3">
              <Skeleton className="h-3 w-32" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return <div className="text-error">Error loading summary data</div>;
  }

  const stats: DashboardStats = { ...defaultStats, ...(data ?? {}) };

  const summaryItems = [
    {
      title: "Total Applications",
      value: stats.totalApplications,
      icon: <Blocks className="w-5 h-5" />,
      iconColor: "bg-blue-100 text-primary",
      changeValue: `${stats.applicationsGrowth}%`,
      isIncrease: stats.applicationsGrowth >= 0,
      changeText: "vs. last month",
      href: "/applications",
    },
    {
      title: "Active Licenses",
      value: stats.activeLicenses,
      icon: <KeySquare className="w-5 h-5" />,
      iconColor: "bg-green-100 text-success",
      changeValue: `${stats.licensesGrowth}%`,
      isIncrease: stats.licensesGrowth >= 0,
      changeText: "vs. last month",
      href: "/licenses",
    },
    {
      title: "Today's Renewals",
      value: stats.todayRenewals === 0 ? "None" : stats.todayRenewals,
      icon: <DollarSign className="w-5 h-5" />,
      iconColor: "bg-red-100 text-error",
      changeValue: formatCurrency(stats.todayRenewalsCost),
      isIncrease: false,
      changeText: "total cost",
      href: "/renewals",
    },
    {
      title: "This Week's Renewals",
      value: stats.weekRenewals === 0 ? "None" : stats.weekRenewals,
      icon: <WrenchIcon className="w-5 h-5" />,
      iconColor: "bg-amber-100 text-warning",
      changeValue: formatCurrency(stats.weekRenewalsCost),
      isIncrease: false,
      changeText: "total cost",
      href: "/renewals",
    },
    {
      title: "Missed / Expired",
      value: stats.missedRenewals === 0 ? "None" : stats.missedRenewals,
      icon: <AlertTriangle className="w-5 h-5" />,
      iconColor: "bg-red-100 text-red-600",
      changeValue: formatCurrency(stats.missedRenewalsCost),
      isIncrease: false,
      changeText: "overdue cost",
      href: "/licenses",
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 mb-6">
      {summaryItems.map((item, index) => (
        <SummaryCard key={index} {...item} />
      ))}
    </div>
  );
}
